import { z } from '@tasama/utils';

export const BANK_OPTIONS = [
  { value: 'jp-morgan', labelKey: 'companySetup.bankOptions.jp-morgan' },
  { value: 'sab', labelKey: 'companySetup.bankOptions.sab' },
  { value: 'snb', labelKey: 'companySetup.bankOptions.snb' },
  { value: 'enbd', labelKey: 'companySetup.bankOptions.enbd' },
  { value: 'other', labelKey: 'companySetup.bankOptions.other' },
];

export const getCompanySetupFormSchema = (t: (_key: string) => string) => {
  const ns = 'companySetup.validation';
  const tradeNameRequired = (n: number) =>
    t(`${ns}.tradeName.required`).replace('#{number}', String(n));
  const tradeNameMax = t(`${ns}.tradeName.max`);

  return z
    .object({
      preferredTradeName1: z
        .string()
        .min(1, tradeNameRequired(1))
        .max(255, tradeNameMax),
      preferredTradeName2: z
        .string()
        .min(1, tradeNameRequired(2))
        .max(255, tradeNameMax),
      preferredTradeName3: z
        .string()
        .min(1, tradeNameRequired(3))
        .max(255, tradeNameMax),
      preferredTradeName4: z
        .string()
        .min(1, tradeNameRequired(4))
        .max(255, tradeNameMax),
      preferredTradeName5: z
        .string()
        .min(1, tradeNameRequired(5))
        .max(255, tradeNameMax),
      preferredBank: z.string().min(1, t(`${ns}.preferredBank.required`)),
      preferredBankOther: z
        .string()
        .max(255, t(`${ns}.preferredBankOther.max`))
        .optional(),
    })
    .refine(
      (data) => {
        if (data.preferredBank === 'other') {
          return !!data.preferredBankOther?.trim();
        }
        return true;
      },
      {
        message: t(`${ns}.preferredBankOther.required`),
        path: ['preferredBankOther'],
      }
    )
    .superRefine((data, ctx) => {
      const tradeNames = [
        {
          field: 'preferredTradeName1' as const,
          value: data.preferredTradeName1,
        },
        {
          field: 'preferredTradeName2' as const,
          value: data.preferredTradeName2,
        },
        {
          field: 'preferredTradeName3' as const,
          value: data.preferredTradeName3,
        },
        {
          field: 'preferredTradeName4' as const,
          value: data.preferredTradeName4,
        },
        {
          field: 'preferredTradeName5' as const,
          value: data.preferredTradeName5,
        },
      ];

      const valueMap = new Map<string, typeof tradeNames>();
      tradeNames.forEach((item) => {
        const normalized = item.value?.trim().toLowerCase();
        if (normalized && normalized.length > 0) {
          if (!valueMap.has(normalized)) valueMap.set(normalized, []);
          valueMap.get(normalized)!.push(item);
        }
      });

      valueMap.forEach((fields) => {
        if (fields.length > 1) {
          fields.forEach(({ field }) => {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t(`${ns}.tradeName.duplicate`),
              path: [field],
            });
          });
        }
      });
    });
};

export const companySetupFormSchema = getCompanySetupFormSchema((key) => key);

export type CompanySetupFormValues = z.infer<typeof companySetupFormSchema>;

export const companySetupFormDefaultValues: Partial<CompanySetupFormValues> = {
  preferredTradeName1: '',
  preferredTradeName2: '',
  preferredTradeName3: '',
  preferredTradeName4: '',
  preferredTradeName5: '',
  preferredBank: '',
  preferredBankOther: '',
};
